import React from 'react';
import {
  FileCheck2,
  Sparkles,
  ShieldCheck,
  Share2,
  Check,
  Printer,
  Eye,
} from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';

interface CertificateActionHeaderProps {
  user: User | null;
  previewTab: 'pdf' | 'crypto' | 'summary';
  onTabChange: (tab: 'pdf' | 'crypto' | 'summary') => void;
  copiedLink: boolean;
  onCopyVerificationLink: () => void;
  onPrint: () => void;
  onOpenLoginModal: () => void;
}

export const CertificateActionHeader: React.FC<CertificateActionHeaderProps> = ({
  user,
  previewTab,
  onTabChange,
  copiedLink,
  onCopyVerificationLink,
  onPrint,
  onOpenLoginModal,
}) => {
  // If no logged in user (public guest), we hide the tabs (Kriptografi, Metadata) and the actions
  if (!user) {
    return (
      <div className="px-4 sm:px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <FileCheck2 className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="font-bold text-slate-800">Pratinjau E-Sertifikat Digital</span>
        </div>

        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200/80 font-bold text-[11px]">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Terdaftar & Terverifikasi</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs">
      {/* Segmented Control Tabs */}
      <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200/80 shadow-2xs w-full md:w-auto overflow-x-auto gap-0.5">
        <button
          type="button"
          onClick={() => onTabChange('pdf')}
          className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-bold text-xs tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5 min-h-[44px] ${
            previewTab === 'pdf'
              ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/40'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
          }`}
        >
          <FileCheck2 className="w-3.5 h-3.5 shrink-0" />
          <span>Pratinjau PDF</span>
        </button>

        <button
          type="button"
          onClick={() => onTabChange('crypto')}
          className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-bold text-xs tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5 min-h-[44px] ${
            previewTab === 'crypto'
              ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/40'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <span>Kriptografi & Hash</span>
        </button>

        <button
          type="button"
          onClick={() => onTabChange('summary')}
          className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-bold text-xs tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5 min-h-[44px] ${
            previewTab === 'summary'
              ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/40'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
          <span>Metadata Sertifikat</span>
        </button>
      </div>

      {/* Quick Action Buttons */}
      <div className="flex items-center gap-2 w-full md:w-auto justify-end">
        <span className="hidden lg:inline text-slate-500 text-[11px] font-medium mr-1">
          Diakses oleh: <strong className="text-slate-800 font-semibold">{user.name}</strong>
        </span>

        <button
          id="btn-copy-cert-link"
          type="button"
          onClick={onCopyVerificationLink}
          className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-bold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition duration-150 cursor-pointer min-h-[44px]"
          title="Salin tautan verifikasi online"
        >
          {copiedLink ? (
            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          ) : (
            <Share2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          )}
          <span>{copiedLink ? 'Tersalin' : 'Bagikan Tautan'}</span>
        </button>

        <button
          id="btn-print-cert"
          type="button"
          onClick={onPrint}
          className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-bold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition duration-150 cursor-pointer min-h-[44px]"
          title="Cetak Dokumen Langsung"
        >
          <Printer className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <span>Cetak</span>
        </button>
      </div>
    </div>
  );
};
